import { addSuppressedError } from '@shared/utils/logger';

export type RenditionSize = 'avatar' | 'tiny' | 'small' | 'medium' | 'large' | 'xlarge' | 'full';

/**
 * Whether a URL can carry rendition params at all.
 *
 * Everything here parses with `new URL()`, which behaves badly on two inputs callers legitimately
 * hold. A relative or protocol-relative path throws, and the catch reports a suppressed error — on
 * every render, for a URL that was never a photo-service request. A `data:` URI is worse: it parses,
 * so a param lands as a `?rendition=…` suffix on the base64 payload and breaks the image.
 *
 * Only absolute http(s) URLs reach a photo service that can serve renditions, so only those are
 * rewritten; anything else is returned untouched.
 */
const isRenditionable = (url: string): boolean => /^https?:\/\//i.test(url);

export const addRendition = <T>({ url, renditionSize, isProduct, quality }: { url: T; renditionSize?: RenditionSize; isProduct?: boolean; quality?: number }): T | string => {
  if (typeof url === 'string' && isRenditionable(url)) {
    try {
      const photoRequestUrl = new URL(url);
      if (renditionSize) {
        photoRequestUrl.searchParams.set('rendition', renditionSize);
      }
      if (isProduct) {
        photoRequestUrl.searchParams.set('product', 'true');
      }
      // Compression hint for the photo service — expired-storage galleries pass 1 so the blurred
      // previews resolve to variants that aren't worth scraping.
      if (quality !== undefined) {
        photoRequestUrl.searchParams.set('quality', String(quality));
      }
      return photoRequestUrl.toString();
    } catch (err) {
      addSuppressedError(err, { context: 'addRendition', url });
    }
  }
  return url;
};

/**
 * Requests an exact pixel width instead of a named bucket.
 *
 * Named renditions size the photo's *shorter* dimension, so the resulting width depends on each
 * photo's aspect ratio — the same name yields a different width for portrait, landscape and 16:9.
 * That makes named buckets unusable as `srcSet` `w` descriptors, which must state real widths.
 *
 * The service will not upscale, so a request wider than the source returns the source width.
 */
export const addWidth = (url: string, width: number): string => {
  if (isRenditionable(url)) {
    try {
      const photoRequestUrl = new URL(url);
      photoRequestUrl.searchParams.set('width', String(width));
      return photoRequestUrl.toString();
    } catch (err) {
      addSuppressedError(err, { context: 'addWidth', url });
    }
  }
  return url;
};

/**
 * Inverse of `addRendition`: strips the `rendition` query param so the original,
 * full-resolution asset is fetched. Use this for downloads — display surfaces should
 * keep their rendition for performance, but downloads must always be the uncompressed original.
 * Other params (e.g. `product`) are preserved. Returns the input unchanged if it is not a parseable URL.
 */
export const removeRendition = (url: string): string => {
  if (isRenditionable(url)) {
    try {
      const photoRequestUrl = new URL(url);
      photoRequestUrl.searchParams.delete('rendition');
      return photoRequestUrl.toString();
    } catch (err) {
      addSuppressedError(err, { context: 'removeRendition', url });
    }
  }
  return url;
};

/** Rendition served to downloads that lack the full-resolution entitlement. */
export const DOWNLOAD_FALLBACK_RENDITION: RenditionSize = 'medium';

/**
 * Picks the URL for a photo download based on the caller's entitlement:
 * full-resolution original when allowed, otherwise a `medium` rendition.
 * Gate the boolean with `usePaidFeature('highResPhotosDownload')` at the call site.
 */
export const resolveDownloadUrl = (url: string, canDownloadFullRes: boolean): string =>
  canDownloadFullRes ? removeRendition(url) : addRendition({ url, renditionSize: DOWNLOAD_FALLBACK_RENDITION });
